// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLinesList, { CartLinesListStyles } from "@conklin/Overrides/Components/CartLinesList";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { showSavingsAmount, showSavingsPercent } = getSettingsCollection(state).productSettings;
    return {
        cart: getCartState(state, state.pages.requisitionConfirmation.cartId).value,
        promotionsDataView: getPromotionsDataView(state, state.pages.requisitionConfirmation.cartId),
        showSavingsAmount,
        showSavingsPercent,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface RequisitionConfirmationProductListStyles {
    centeringWrapper?: InjectableCss;
    cartLinesList?: CartLinesListStyles;
}

export const requisitionConfirmationProductListStyles: RequisitionConfirmationProductListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
    cartLinesList: {
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
        cardExpanded: {
            productDescriptionAndPartNumbersGridItem: { width: [12, 12, 6, 12, 6] },
            productPriceAndQuantityGridItem: { width: [12, 12, 6, 12, 6] },
        },
    },
};

const styles = requisitionConfirmationProductListStyles;

const RequisitionConfirmationProductList = ({
    cart,
    promotionsDataView,
    showSavingsAmount,
    showSavingsPercent,
}: Props) => {
    if (!cart || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <CartLinesList
            cart={cart}
            promotions={promotionsDataView.value}
            isCondensed={false}
            hideCondensedSelector={true}
            editable={false}
            showSavingsAmount={showSavingsAmount}
            showSavingsPercent={showSavingsPercent}
            extendedStyles={styles.cartLinesList}
            // XNG-Changes
            {...{ showEditCart: false }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RequisitionConfirmationProductList),
    definition: {
        displayName: "Product List",
        allowedContexts: ["RequisitionConfirmationPage"],
        group: "Requisition Confirmation",
    },
};

export default widgetModule;
